import { ScheduledAction } from '../../shared/interfaces/account.interfaces';

/**
 * Utility functions to convert between UTC and Local Time for Scheduled Actions.
 */

// Helper to check if a string is a valid date
function parseUtcDate(utcStr: string | undefined): Date | null {
  if (!utcStr) return null;
  // If it's already an ISO string with Z or timezone offset, just parse it
  if (utcStr.includes('Z') || utcStr.includes('+') || (utcStr.includes('-') && utcStr.split('-').length > 3)) {
    const d = new Date(utcStr);
    return isNaN(d.getTime()) ? null : d;
  }
  // Otherwise, format: "YYYY-MM-DD HH:mm:ss" -> replace space with "T" and add "Z"
  const isoStr = utcStr.replace(' ', 'T') + 'Z';
  const d = new Date(isoStr);
  return isNaN(d.getTime()) ? null : d;
}

// Convert UTC timestamp (e.g. from backend) to a Local ISO String (so Angular's Date pipe displays local time)
export function utcToLocalTimestamp(utcStr: string | undefined): string | undefined {
  if (!utcStr) return utcStr;
  const date = parseUtcDate(utcStr);
  if (!date) return utcStr;
  return date.toISOString();
}

// Convert UTC datetime-trigger "at" value to local "YYYY-MM-DD HH:mm:ss" format
export function utcToLocalDatetime(utcStr: string | undefined): string {
  if (!utcStr) return '';
  const date = parseUtcDate(utcStr);
  if (!date) return utcStr || '';
  
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

// Convert local datetime-trigger "at" value to UTC ISO format "YYYY-MM-DDTHH:mm:ss.sssZ"
export function localToUtcDatetime(localStr: string | undefined): string {
  if (!localStr) return '';
  // datetime-local input value could be "YYYY-MM-DDTHH:mm" or "YYYY-MM-DD HH:mm:ss"
  const formattedInput = localStr.replace(' ', 'T');
  const date = new Date(formattedInput);
  if (isNaN(date.getTime())) return localStr || '';
  
  return date.toISOString();
}

// Shift a cron expression between local time and UTC
// toUtc = true: local cron -> UTC cron
// toUtc = false: UTC cron -> local cron
export function shiftCron(cron: string, toUtc: boolean): string {
  const parts = cron.split(' ');
  if (parts.length !== 5) return cron;

  let [minStr, hourStr, dayStr, monthStr, weekdayStr] = parts;

  // Get timezone offset in minutes
  const offset = new Date().getTimezoneOffset(); // e.g. 180 for UTC-3
  const shiftMinutes = toUtc ? offset : -offset;

  if (shiftMinutes === 0) return cron;

  // Parse single hour and minute (the UI only allows selecting a single hour/minute)
  let hour = parseInt(hourStr, 10);
  let min = parseInt(minStr, 10);

  if (isNaN(hour) || isNaN(min)) {
    // If hour or minute are not numbers (e.g. '*'), we don't shift
    return cron;
  }

  // Calculate new time
  let totalMinutes = hour * 60 + min + shiftMinutes;
  
  let dayDelta = 0;
  if (totalMinutes < 0) {
    dayDelta = -1;
    totalMinutes += 24 * 60;
  } else if (totalMinutes >= 24 * 60) {
    dayDelta = 1;
    totalMinutes -= 24 * 60;
  }
  
  const newHour = Math.floor(totalMinutes / 60);
  const newMin = totalMinutes % 60;

  // Update weekday if not '*'
  let newWeekdayStr = weekdayStr;
  if (weekdayStr !== '*') {
    newWeekdayStr = weekdayStr.split(',').map(w => {
      let val = parseInt(w, 10);
      if (isNaN(val)) return w;
      val = (val + dayDelta) % 7;
      if (val < 0) val += 7;
      return val.toString();
    }).join(',');
  }

  // Update day of month if not '*'
  let newDayStr = dayStr;
  if (dayStr !== '*') {
    newDayStr = dayStr.split(',').map(d => {
      let val = parseInt(d, 10);
      if (isNaN(val)) return d;
      // Day is 1-indexed (1 to 31)
      val = val + dayDelta;
      if (val < 1) val = 31;
      else if (val > 31) val = 1;
      return val.toString();
    }).join(',');
  }

  return `${newMin} ${newHour} ${newDayStr} ${monthStr} ${newWeekdayStr}`;
}

// Maps ScheduledAction coming from backend (UTC) to frontend (Local)
export function mapScheduledActionToLocal(action: ScheduledAction): ScheduledAction {
  const mapped = { ...action };
  
  if (mapped.triggered_at) {
    mapped.triggered_at = utcToLocalTimestamp(mapped.triggered_at);
  }
  if (mapped.created_at) {
    mapped.created_at = utcToLocalTimestamp(mapped.created_at);
  }
  
  if (mapped.trigger) {
    const trigger = { ...mapped.trigger };
    if (trigger.type === 'datetime') {
      trigger.at = utcToLocalDatetime(trigger.at);
    } else if (trigger.type === 'cron') {
      trigger.schedule = shiftCron(trigger.schedule, false);
    }
    mapped.trigger = trigger;
  }
  
  return mapped;
}

// Maps ScheduledAction going from frontend (Local) to backend (UTC)
export function mapScheduledActionToUtc(action: ScheduledAction): ScheduledAction {
  const mapped = { ...action };
  
  if (mapped.trigger) {
    const trigger = { ...mapped.trigger };
    if (trigger.type === 'datetime') {
      trigger.at = localToUtcDatetime(trigger.at);
    } else if (trigger.type === 'cron') {
      trigger.schedule = shiftCron(trigger.schedule, true);
    }
    mapped.trigger = trigger;
  }
  
  return mapped;
}
